import { apiSlice } from "./apiSlice";

export const planApi = apiSlice
    .enhanceEndpoints({
        addTagTypes: ['PLAN', 'PLAN_SUBSCRIPTION'],
    })
    .injectEndpoints({
        keepUnusedDataFor: 1,
        endpoints: builder => ({
            getPlans: builder.query({
                query: () => ({
                    url: `/plan/admin`,
                    method: 'GET'
                }),
                providesTags: (result) =>
                    result
                        ? [
                            ...result.map(({ id }) => ({ type: 'PLAN', id })),
                            { type: 'PLAN', id: 'LIST' }
                        ]
                        : [{ type: 'PLAN', id: 'LIST' }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            getPlansPublic: builder.query({
                query: () => ({
                    url: `/plan`,
                    method: 'GET'
                }),
                providesTags: (result) =>
                    result
                        ? [
                            ...result.map(({ id }) => ({ type: 'PLAN', id })),
                            { type: 'PLAN', id: 'LIST' }
                        ]
                        : [{ type: 'PLAN', id: 'LIST' }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            getPlanById: builder.query({
                query: (id) => ({
                    url: `/plan/${id}`,
                    method: 'GET'
                }),
                providesTags: (result, error, id) => [{ type: 'PLAN', id }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            createPlan: builder.mutation({
                query: (payload) => ({
                    url: `/plan`,
                    method: 'POST',
                    body: { ...payload }
                }),
                invalidatesTags: [{ type: 'PLAN', id: 'LIST' }],
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            updatePlan: builder.mutation({
                query: ({ id, payload }) => ({
                    url: `/plan/${id}`,
                    method: 'PUT',
                    body: { ...payload }
                }),
                invalidatesTags: (result, error, { id }) => [{ type: 'PLAN', id }, { type: 'PLAN', id: 'LIST' }],
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            subscribeToPlan: builder.mutation({
                query: (payload) => ({
                    url: `/plan/subscribe-plan`,
                    method: 'POST',
                    body: { ...payload }
                }),
                invalidatesTags: [{ type: 'PLAN', id: 'LIST' }],
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            deletePlan: builder.mutation({
                query: (id) => ({
                    url: `/plan/${id}`,
                    method: 'DELETE'
                }),
                invalidatesTags: (result, error, id) => [{ type: 'PLAN', id }, { type: 'PLAN', id: 'LIST' }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            getSubscriptionInfo: builder.query({
                query: () => ({
                    url: `/plan-subscription/my-subscription`,
                    method: 'GET'
                }),
                providesTags: ['PLAN_SUBSCRIPTION'],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            getSubscriptionsLogsByAdmin: builder.query({
                query: (params) => ({
                    url: `/plan-subscription`,
                    method: 'GET',
                    params
                }),
                providesTags: ['PLAN_SUBSCRIPTION'],
                 transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            subscripeToPlan: builder.mutation({
                query: (payload) => ({
                    url: `/plan-subscription`,
                    method: 'POST',
                    body: { ...payload }
                }),
                invalidatesTags: ['PLAN_SUBSCRIPTION'],
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            cancelSubscription: builder.mutation({
                query: (payload) => ({
                    url: `/plan-subscription/cancel`,
                    method: 'PUT',
                    body: { ...payload }
                }),
                invalidatesTags: ['PLAN_SUBSCRIPTION'],
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            getSubscriptionForMerchantByAdmin: builder.query({
                query: (merchantId) => ({
                    url: `/plan-subscription/${merchantId}/admin`,
                    method: 'GET',
                }),
                providesTags: ['ADMIN_PLAN_SUBSCRIPTION'],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            getEditPlanLogsForMerchant: builder.query({
                query: (params) => ({
                    url: `/subscription-log`,
                    method: 'GET',
                    params
                }),
                providesTags: ['ADMIN_PLAN_SUBSCRIPTION'],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            updateMerchantSubscription: builder.mutation({
                query: ({ merchantId, payload }) => ({
                    url: `/plan-subscription/${merchantId}/admin`,
                    method: 'PUT',
                    body: { ...payload }
                }),
                invalidatesTags: ['ADMIN_PLAN_SUBSCRIPTION'],
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            applyCoupon: builder.mutation({
                query: ({ planId, payload }) => ({
                    url: `/plan/apply-coupon/${planId}`,
                    method: 'PUT',
                    body: { ...payload }
                }),
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            })
        })
    })

export const {
    useGetPlansQuery,
    useGetPlanByIdQuery,
    useCreatePlanMutation,
    useUpdatePlanMutation,
    useDeletePlanMutation,
    useSubscribeToPlanMutation,
    useGetSubscriptionInfoQuery,
    useLazyGetSubscriptionInfoQuery,
    useGetPlansPublicQuery,
    useSubscripeToPlanMutation,
    useCancelSubscriptionMutation,
    useGetSubscriptionForMerchantByAdminQuery,
    useUpdateMerchantSubscriptionMutation,
    useApplyCouponMutation,
    useGetEditPlanLogsForMerchantQuery,
    useGetSubscriptionsLogsByAdminQuery,
    useLazyGetSubscriptionsLogsByAdminQuery
} = planApi